import './subeventhero.less'

import {Background, Icon, Image} from '@codeurs/front'
import FwddiveLogoWhite from 'assets/images/logos/fwddive-logo-white.svg'
import ItfbelgiumLogoWhite from 'assets/images/logos/itfbelgium-logo-white.svg'
import TinyaiLogoWhite from 'assets/images/logos/tinyai-logo-white.svg'
import React from 'react'
import {ImageType, LinkType} from 'types'
import {Link} from 'util/link'
import {style} from 'util/style'
import {SubeventLogo} from 'views/layout/subeventlogo'
import {Theme} from 'views/theme'

const styles = style('subeventhero', [
	'image',
	'shapes',
	'content',
	'buttons',
	'button'
])

export type SubeventheroType = SubeventheroImageType &
	SubeventheroContentType &
	SubeventheroButtonsType

export type SubeventheroImageType = {
	image?: ImageType
	image_overlay?: number
}

export type SubeventheroContentType = {
	logo?: 'itfbelgium' | 'fwddive' | 'tinyai'
	title: string
	subtitle?: string
	date?: string
	location?: string
}

export type SubeventheroButtonsType = {
	previous?: LinkType
	next?: LinkType
}

export const Subeventhero: React.FC<SubeventheroType> = ({
	image,
	image_overlay,
	logo,
	title,
	subtitle,
	date,
	location,
	previous,
	next
}) => {
	return (
		<Theme.OuterContainer>
			<Theme.Container>
				<div className={styles()}>
					<SubeventheroImage image={image} image_overlay={image_overlay} />
					<SubeventheroShapes />
					<SubeventheroContent
						logo={logo}
						title={title}
						subtitle={subtitle}
						date={date}
						location={location}
					/>
					<SubeventheroButtons previous={previous} next={next} />
				</div>
			</Theme.Container>
		</Theme.OuterContainer>
	)
}

export const SubeventheroImage: React.FC<{
	image: ImageType
	image_overlay?: number
}> = ({image, image_overlay}) => {
	if (!image || image.empty) return null
	const opacity = image_overlay ? image_overlay / 100 : 0

	return (
		<div className={styles.image()}>
			<Background img={image} />
			<span className={styles.image('overlay')} style={{opacity}} />
		</div>
	)
}

export const SubeventheroShapes: React.FC = () => (
	<div className={styles.shapes()}>
		<span className={styles.shapes('after')} />
		<span className={styles.shapes('fsshape')} />
		<span className={styles.shapes('stuff')} />
	</div>
)

export const SubeventheroContent: React.FC<SubeventheroContentType> = ({
	logo,
	title,
	subtitle,
	date,
	location
}) => {
	return (
		<div className={styles.content()}>
			{logo && (
				<SubeventLogo subevent={logo} className={styles.content('logo')} />
			)}
			{title && (
				<div className={styles.content('titlecontainer')}>
					<h1 className={styles.content('title')}>
						{title}
						{subtitle && <span>{subtitle}</span>}
					</h1>
				</div>
			)}
			{(date || location) && (
				<div className={styles.content('info')}>
					{date && <p className={styles.content('info-date')}>{date}</p>}
					{location && (
						<p className={styles.content('info-location')}>{location}</p>
					)}
				</div>
			)}
		</div>
	)
}

export const SubeventheroButtons: React.FC<SubeventheroButtonsType> = ({
	previous,
	next
}) => {
	if (!previous && !next) return null

	return (
		<div className={styles.buttons()}>
			<div className={styles.buttons('left')}>
				{previous && (
					<SubeventheroButton theme="fwddive" direction="prev" {...previous} />
				)}
			</div>
			<div className={styles.buttons('right')}>
				{next && (
					<SubeventheroButton theme="tinyai" direction="next" {...next} />
				)}
			</div>
		</div>
	)
}

export const SubeventheroButton: React.FC<{
	direction?: 'prev' | 'next'
	url: string
	title: string
	theme?: 'itfbelgium' | 'fwddive' | 'tinyai'
	date?: string
}> = ({direction, url, title, theme, date}) => {
	return (
		<Link
			to={url}
			className={styles
				.button()
				.mod(direction)
				.mod(theme)}
		>
			<div className={styles.button('content')}>
				{title && <p className={styles.button('content-title')}>{title}</p>}
				{date && <p className={styles.button('content-date')}>{date}</p>}
			</div>
			<div className={styles.button('icon')}>
				<Icon icon="arrow-long" />
			</div>
		</Link>
	)
}
